import React, { useState } from 'react';
import { useAuthAdmin } from '../hooks/useAuthAdmin';
import { ticketService } from '../services/api';
import Layout from './Layout';
import MessageAlerts from './MessageAlerts';
import QRScanner from './QRScanner';
import ManualQRInput from './ManualQRInput';
import TicketInfo from './TicketInfo';
import Instructions from './Instructions';
import TransactionList from './TransactionList';
import TicketList from './TicketList';

// Import the proper Ticket interface from types
import { Ticket } from '../types';

const Dashboard: React.FC = () => {
  const { isAuthenticated, isCheckingAuth, handleLogout } = useAuthAdmin();
  
  // Simple state management without complex hooks
  const [scannedTicket, setScannedTicket] = useState<Ticket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [activeView, setActiveView] = useState<'scanner' | 'transactions' | 'tickets'>('scanner');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Real ticket validation
  const handleScan = async (qrData: string) => {
    console.log('QR Code scanned:', qrData);
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await ticketService.validate(qrData);
      console.log('API Response:', result);
      
      if (result.valid && result.ticket) {
        const validationData = result.ticket;
        const ticket: Ticket = {
          number: validationData.number,
          type: validationData.type,
          date: validationData.date,
          time: validationData.time,
          price: validationData.price,
          status: validationData.status,
          transaction_id: validationData.transaction_id,
          payment_status: validationData.payment_status,
          qr_code: validationData.qr_code,
          barcode: validationData.barcode,
          created_at: validationData.created_at,
          updated_at: validationData.updated_at,
        };
        setScannedTicket(ticket);
        
        // Check ticket status and show appropriate message
        switch (validationData.status) {
          case 'active':
            setSuccess('Ticket is valid and ready to use!');
            break;
          case 'used':
            setError('This ticket has already been used');
            break;
          case 'pending':
            setError('Payment not completed - ticket is pending');
            break;
          case 'expired':
            setError('This ticket has expired');
            break;
          case 'cancelled':
            setError('This ticket has been cancelled');
            break;
          default:
            setError('Unknown ticket status');
        }
      } else {
        setError(result.message || 'Ticket validation failed');
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError('Failed to validate ticket. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseTicket = async () => {
    if (scannedTicket) {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        await ticketService.use(scannedTicket.number);
        setScannedTicket({ ...scannedTicket, status: 'used' });
        setSuccess('Ticket marked as used successfully!');
      } catch (err: any) {
        console.error('Use ticket error:', err);
        const errorMessage = err.response?.data?.message || 'Failed to mark ticket as used. Please try again.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleManualSubmit = (qrData: string) => {
    // Clean the QR data (remove any whitespace)
    const cleanQrData = qrData.trim();
    
    // If it's a transaction ID (starts with TXN-), use it directly
    if (cleanQrData.startsWith('TXN-')) {
      handleScan(cleanQrData);
      return;
    }
    
    // If it's a ticket URL, use it as is
    if (cleanQrData.includes('http') || cleanQrData.includes('/ticket/')) {
      handleScan(cleanQrData);
      return;
    }
    
    // For other formats, try to construct a ticket URL
    const apiUrl = process.env.REACT_APP_API_URL;
    const dataToValidate = `${apiUrl}/ticket/${cleanQrData}`;
    handleScan(dataToValidate);
  };

  const resetScan = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    setScannedTicket(null);
    setError(null);
    setSuccess(null);
    console.log('Reset scanner clicked');
  };

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  return (
    <Layout
      title="Dashboard"
      subtitle="Scan and validate tickets"
      onLogout={handleLogout}
    >
      <MessageAlerts
        success={success}
        error={error}
        cameraError={null}
        onClearError={clearError}
        onClearSuccess={clearSuccess}
        onShowManualInput={() => setShowManualInput(true)}
        onRetryScanner={() => {}}
      />

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-2">
        <div className="border-b border-gray-200">
          <nav className="flex justify-between px-6">
            <div className="flex">
              <button
                onClick={() => setActiveView('scanner')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeView === 'scanner'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Scanner
              </button>
            </div>
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveView('transactions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeView === 'transactions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Transactions
              </button>
              <button
                onClick={() => setActiveView('tickets')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeView === 'tickets'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tickets
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Content based on active view */}
      {activeView === 'scanner' && (
        <>
          {/* QR Scanner */}
          {!scannedTicket && (
            <>
               <QRScanner
                 onScan={handleScan}
                 isProcessing={isLoading}
                 onError={setError}
                 onReset={resetScan}
               />
              
              <ManualQRInput
                onValidate={handleManualSubmit}
                isLoading={isLoading}
                isVisible={showManualInput}
                onToggle={() => setShowManualInput(!showManualInput)}
              />
            </>
          )}

          {/* Scanned Ticket Details */}
          {scannedTicket && (
            <TicketInfo
              ticket={scannedTicket}
              onUseTicket={handleUseTicket}
              onScanAnother={resetScan}
              isLoading={isLoading}
              isResettingScanner={isLoading}
            />
          )}

          <Instructions />
        </>
      )}

      {activeView === 'transactions' && (
        <TransactionList refreshTrigger={refreshTrigger} />
      )}

      {activeView === 'tickets' && (
        <TicketList refreshTrigger={refreshTrigger} />
      )}
    </Layout>
  );
};

export default Dashboard;
